<!-- 通知消息详情：标题 / 时间 / 内容（支持 HTML） -->
<template>
  <div class="panel-detail">
    <header class="detail-header">
      <button
        type="button"
        class="back-btn"
        :aria-label="$t('backToMessages')"
        @click="emit('back')"
      >
        <el-icon :size="18">
          <ArrowLeft />
        </el-icon>
      </button>
      <h3 class="detail-header-title">{{ $t('messageDetail') }}</h3>
    </header>

    <div class="detail-body">
      <template v-if="detail">
        <div class="detail-meta">
          <h4 class="detail-title">{{ detail.title }}</h4>
          <time class="detail-time">{{
            formatNotificationTime(detail.createTime)
          }}</time>
        </div>
        <article class="detail-content-wrap">
          <div
            class="detail-content"
            v-html="formatNotificationContent(detail.content)"
          />
        </article>
      </template>
      <div v-else-if="error" class="panel-empty">
        {{ $t('loadFailed') }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { inject } from 'vue'
import { ArrowLeft } from '@element-plus/icons-vue'
import type { NotificationItem } from '@/api/notification'
import { formatNotificationTime } from './useNotification'
import { formatNotificationContent } from './notificationContent'

defineProps<{
  detail: NotificationItem | null
  error: boolean
}>()

const emit = defineEmits<{
  back: []
}>()

const $t: any = inject('$t')
</script>

<style lang="scss" scoped>
.panel-detail {
  display: flex;
  flex-direction: column;
  width: 600px;
  max-width: 600px;
  min-height: 800px;
  height: 800px;
  max-height: 800px;
  overflow: hidden;
  color: #303133;
}

.detail-header {
  flex-shrink: 0;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 42px;
  padding: 0 44px 14px;
  border-bottom: 1px solid #ebeef5;
  margin-bottom: 0;
}

.back-btn {
  position: absolute;
  left: 0;
  top: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  padding: 0;
  border: none;
  background: transparent;
  color: var(--el-color-primary);
  cursor: pointer;
  border-radius: 8px;
  transition: background-color 0.2s;

  &:hover {
    background-color: var(--el-color-primary-light-9);
  }

  &:active {
    background-color: var(--el-color-primary-light-8);
  }
}

.detail-header-title {
  margin: 0;
  font-size: 16px;
  font-weight: 650;
  line-height: 1.4;
  color: #303133;
}

.detail-body {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.detail-meta {
  flex-shrink: 0;
  padding: 0 4px 12px;
  border-bottom: 1px solid #eef0f4;
}

.detail-title {
  margin: 0;
  font-size: 17px;
  font-weight: 650;
  line-height: 1.55;
  color: #303133;
  word-break: break-word;
  white-space: normal;
}

.detail-time {
  display: block;
  margin-top: 8px;
  font-size: 12px;
  line-height: 1.3;
  color: #a8abb2;
}

.detail-content-wrap {
  flex: 1;
  min-height: 0;
  min-width: 0;
  margin-top: 12px;
  padding: 12px 4px 4px 0;
  overflow-x: auto;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-gutter: stable;

  &::-webkit-scrollbar {
    width: 5px;
    height: 5px;
  }

  &::-webkit-scrollbar-thumb {
    background: #dcdfe6;
    border-radius: 5px;
  }
}

.detail-content {
  min-width: 0;
  font-size: 14px;
  line-height: 1.75;
  color: #606266;
  overflow-wrap: anywhere;
  word-break: normal;

  :deep(p) {
    margin: 0 0 12px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  :deep(a) {
    color: var(--el-color-primary);
    word-break: break-all;
  }

  :deep(ul),
  :deep(ol) {
    margin: 0 0 12px;
    padding-left: 22px;
  }

  :deep(br) {
    display: block;
    content: '';
    margin-top: 4px;
  }

  :deep(img),
  :deep(video),
  :deep(svg) {
    display: block;
    max-width: 100%;
    height: auto;
    margin: 12px auto;
    border-radius: 8px;
  }

  :deep(table) {
    width: max-content;
    min-width: 100%;
    border-collapse: collapse;
    background: #fff;
  }

  :deep(th),
  :deep(td) {
    padding: 8px 10px;
    border: 1px solid #dfe3e8;
    text-align: left;
    white-space: nowrap;
  }

  :deep(pre) {
    max-width: 100%;
    margin: 12px 0;
    padding: 12px;
    border-radius: 8px;
    background: #282c34;
    color: #f4f4f5;
    overflow-x: auto;
    white-space: pre;
  }

  :deep(blockquote) {
    margin: 12px 0;
    padding: 8px 12px;
    border-left: 3px solid var(--el-color-primary-light-5);
    background: #fff;
    color: #73767a;
  }

  :deep(h1),
  :deep(h2),
  :deep(h3),
  :deep(h4),
  :deep(h5),
  :deep(h6) {
    margin: 18px 0 8px;
    color: #303133;
    line-height: 1.4;
  }

  :deep(hr) {
    margin: 16px 0;
    border: 0;
    border-top: 1px solid #dfe3e8;
  }
}

.panel-empty {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100px;
  color: #909399;
  font-size: 13px;
}
</style>
